<template>
  <div class="buttons are-small">
    <button
      :class="option == 'this month' ? 'is-link is-active' : 'is-light'"
      class="button"
      @click="option = 'this month'"
    >
      This Month
    </button>
    <button
      :class="option == 'this year' ? 'is-link is-active' : 'is-light'"
      class="button"
      @click="option = 'this year'"
    >
      This Year
    </button>
    <button
      :class="option == 'all time' ? 'is-link is-active' : 'is-light'"
      class="button"
      @click="option = 'all time'"
    >
      All Time
    </button>
  </div>
  <div ref="chart" class="chart" />
  <table class="table is-fullwidth is-narrow">
    <tbody>
      <tr>
        <td>Income</td>
        <td v-if="totalIncome > 0" class="has-text-right">%</td>
        <td class="has-text-right">
          {{ $options.numberFormat.format(totalIncome) }}
        </td>
      </tr>
      <tr>
        <td>Investments</td>
        <td v-if="totalIncome > 0" class="has-text-right">
          {{ ((totalInvestments / totalIncome) * 100).toFixed(1) }}%
        </td>
        <td class="has-text-right">
          {{ $options.numberFormat.format(totalInvestments) }}
        </td>
      </tr>
      <tr>
        <td>Expenses</td>
        <td v-if="totalIncome > 0" class="has-text-right">
          {{ ((totalExpenses / totalIncome) * 100).toFixed(1) }}%
        </td>
        <td class="has-text-right">
          {{ $options.numberFormat.format(totalExpenses) }}
        </td>
      </tr>
      <tr>
        <td>Balance</td>
        <td v-if="totalIncome > 0" class="has-text-right">
          {{ ((totalBalance / totalIncome) * 100).toFixed(1) }}%
        </td>
        <td
          :class="
            totalBalance >= 0 ? 'has-text-success-dark' : 'has-text-danger-dark'
          "
          class="has-text-right has-text-weight-semibold"
        >
          {{ totalBalance >= 0 ? '+' : ''
          }}{{ $options.numberFormat.format(totalBalance) }}
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import vegaEmbed from 'vega-embed';
import { createNamespacedHelpers } from 'vuex';
import { dateToSerialNumber } from '@/utils';
const { mapState } = createNamespacedHelpers('balanceSheet');

export default {
  numberFormat: new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 2,
  }),

  data() {
    return {
      option: 'this month',
    };
  },

  computed: {
    ...mapState(['expenseItems', 'investmentItems', 'incomeItems']),

    dateRange() {
      const now = new Date();

      switch (this.option) {
        case 'this year':
          return [
            dateToSerialNumber(new Date(now.getFullYear(), 0, 1)),
            dateToSerialNumber(new Date(now.getFullYear() + 1, 0, 0)),
          ];
        case 'this month':
          return [
            dateToSerialNumber(new Date(now.getFullYear(), now.getMonth(), 1)),
            dateToSerialNumber(
              new Date(now.getFullYear(), now.getMonth() + 1, 0)
            ),
          ];
        default:
          return null;
      }
    },

    expenseItemsFiltered() {
      return this.dateRange
        ? this.expenseItems.filter(
            i => i[0] >= this.dateRange[0] && i[0] <= this.dateRange[1]
          )
        : this.expenseItems;
    },

    investmentItemsFiltered() {
      return this.dateRange
        ? this.investmentItems.filter(
            i => i[0] >= this.dateRange[0] && i[0] <= this.dateRange[1]
          )
        : this.investmentItems;
    },

    incomeItemsFiltered() {
      return this.dateRange
        ? this.incomeItems.filter(
            i => i[0] >= this.dateRange[0] && i[0] <= this.dateRange[1]
          )
        : this.incomeItems;
    },

    totalExpenses() {
      return this.expenseItemsFiltered.reduce((total, i) => total + i[2], 0);
    },

    totalInvestments() {
      return this.investmentItemsFiltered.reduce((total, i) => total + i[3], 0);
    },

    totalIncome() {
      return this.incomeItemsFiltered.reduce((total, i) => total + i[1], 0);
    },

    totalBalance() {
      return this.totalIncome - this.totalExpenses - this.totalInvestments;
    },

    values() {
      return [
        {
          type: 'Income',
          total: this.totalIncome,
          color: '#2ca02c',
        },
        {
          type: 'Investments',
          total: this.totalInvestments,
          color: '#1f77b4',
        },
        {
          type: 'Expenses',
          total: this.totalExpenses,
          color: '#d62728',
        },
      ];
    },
  },

  watch: {
    values() {
      this.vegaView.data('summary', this.values).run();
    },
  },

  async mounted() {
    const { view } = await this.plot();

    window.vegaView = view;

    this.vegaView = view;
  },

  methods: {
    plot() {
      const spec = {
        $schema: 'https://vega.github.io/schema/vega-lite/v4.json',
        width: 'container',
        height: 200,
        data: {
          name: 'summary',
          values: this.values,
        },
        mark: 'bar',
        encoding: {
          y: {
            field: 'type',
            type: 'nominal',
            sort: null,
            axis: { labelAngle: 90 },
          },
          x: {
            field: 'total',
            type: 'quantitative',
          },
          color: {
            field: 'color',
            type: 'nominal',
            scale: null,
          },
          tooltip: {
            field: 'total',
            type: 'quantitative',
          },
        },
      };

      return vegaEmbed(this.$refs.chart, spec);
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  height: 200px;
}
</style>
